import { ComponentProps, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import cn from '../../app/utils/cn';
import Loader from './Loader';

export type ButtonColors = 'primary' | 'outline' | 'danger' | 'ghost';
export type ButtonSize = 'default' | 'icon';

interface IButtonProps extends ComponentProps<'button'> {
  labelPath?: string;
  color?: ButtonColors;
  size?: ButtonSize;
  isLoading?: boolean;
  children?: React.ReactNode;
}

const SOLID_DISABLED_CLASS =
  'disabled:border-grey-200 disabled:bg-grey-100 disabled:text-grey-500 ' +
  'dark:disabled:border-grey-600 dark:disabled:bg-grey-700 dark:disabled:text-grey-400';

const OUTLINE_DISABLED_CLASS =
  `${SOLID_DISABLED_CLASS} ` +
  'disabled:hover:bg-grey-100 disabled:hover:text-grey-500 ' +
  'dark:disabled:hover:bg-grey-700 dark:disabled:hover:text-grey-400';

const GHOST_DISABLED_CLASS =
  `${SOLID_DISABLED_CLASS} ` +
  'disabled:hover:bg-grey-100 dark:disabled:hover:bg-grey-700';

const GHOST_ICON_DISABLED_CLASS =
  'disabled:text-grey-400 dark:disabled:text-grey-500';

function getDisabledClass(color: ButtonColors, isIcon: boolean) {
  if (color === 'ghost') {
    return isIcon ? GHOST_ICON_DISABLED_CLASS : GHOST_DISABLED_CLASS;
  }

  if (color === 'outline') {
    return OUTLINE_DISABLED_CLASS;
  }

  return SOLID_DISABLED_CLASS;
}

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      labelPath,
      type = 'button',
      className,
      color = 'primary',
      size = 'default',
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const labelContent = labelPath
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        t(labelPath as any)
      : '';

    const isIcon = size === 'icon';

    return (
      <button
        ref={ref}
        className={cn(
          'flex items-center justify-center tracking-wide transition-all duration-300 ease-in-out outline-none',
          'focus-visible:ring-2 focus-visible:ring-green-500/50',
          'disabled:pointer-events-none',
          !isIcon && 'h-10 w-full truncate rounded-lg px-10',
          isIcon && 'h-10 w-10 rounded-full p-2',
          color === 'primary' &&
            'text-white-0 border border-green-600 bg-green-600 hover:bg-green-700',
          color === 'outline' &&
            'bg-surface-card hover:text-white-0 border border-green-600 text-green-600 hover:bg-green-600 ' +
              'dark:border-green-400 dark:text-green-400 dark:hover:bg-green-600',
          color === 'danger' &&
            'text-white-0 border border-red-600 bg-red-600 hover:bg-red-700',
          color === 'ghost' &&
            !isIcon &&
            'border-grey-800 bg-surface-card text-text-primary hover:bg-grey-800/5 border ' +
              'dark:border-grey-0 dark:hover:bg-grey-0/5',
          color === 'ghost' &&
            isIcon &&
            'text-text-primary hover:bg-hover-subtle bg-transparent',
          isLoading
            ? 'cursor-progress'
            : `disabled:cursor-not-allowed ${getDisabledClass(color, isIcon)}`,
          className,
        )}
        type={type}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading ? (
          <Loader className="h-6 w-6" tone="current" />
        ) : (
          (children ?? labelContent)
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
